import styles from "@/styles/topReview/topReviewIndex.module.scss"
import { useState, useEffect } from 'react'
import DefaultLayout from "@/components/DefaultLayout"
import ShopInfo from "@/components/ShopInfo"
import MiddleSubscribe from "@/components/MiddleSubscribe"
import CategoryFilter from "@/components/topReview/CategoryFilter"
import TopReviewItem from "@/components/topReview/TopReviewItem"
import CrazySale from "@/components/topReview/CrazySale"
import InterestedItem from "@/components/topReview/InterestedItem"

import type { PaginationProps } from 'antd';
import { Pagination } from 'antd';

import topReviewsApi from '@/service/modules/topReviewsApi'
import systemApi from '@/service/modules/systemApi'

import Head from "next/head"
import { getCookie, setCookie } from '@/utils/cookie'
import { useRouter } from 'next/router'
import GoogleADSense from "@/components/GoogleADSense"
import dayjs from 'dayjs'

import getConfig from 'next/config'
const { publicRuntimeConfig } = getConfig()
const apiDomain = publicRuntimeConfig.apiDomain

interface ProductItem {
  productID: string;
  name: string;
  url: string;
  imgUrl: string;
  reviewCount: number;
  price: number;
  normalPrice: number;
  freeShipping: number;
  onSale: number;
  reduced: number;
  isNew: number;
  position?: number,
  location?: string;
  isEmailOnly: any;
  inStock?: number;
  isGroup?: number;
  preSales?: number;
  promotionTypeID: string | number;
  promotion: {
    productID: number;
    promotionType: number;
    promotionPrice: number;
    normalPrice: number;
    endDate: string;
    status: number;
    notified: number;
    pointRate: string;
    promotionRate: number;
  };
  review: {
    avgRank: number;
    total: number;
  };
  shortDescription?: string;
  visitor?: {
    weeklyVisitors: number;
  }
  rankNum?: number;
}

interface CsotdData {
  productID: string;
  name: string;
  url: string;
  imgUrl: string;
  filename: string;
  review: number;
  reviewCount: number;
  price: number;
  normalPrice: number;
  freeShipping: number;
  onSale: number;
  reduced: number;
  isNew: number;
  position?: number,
  location?: string;
  isEmailOnly: any,
  promotionTypeID: string | number;
  promotion: {
    productID: number;
    promotionType: number;
    promotionPrice: number;
    normalPrice: number;
    endDate: string;
    status: number;
    notified: number;
    pointRate: string;
    promotionRate: number;
  };
  crazyDeal: {
    hoursLeft: number;
    minutesLeft: number;
    secondsLeft: number;
  } | undefined
}

interface BannerItem {
  imgUrl: string;
  title: string;
  link: string;
}

interface Props {
  topReviewsData: ProductItem[];
  topReviewsTotal: number;
  topReviewsCurrentPage: number;
  param: {
    page: number;
    categoryName: string;
    categoryID: number;
  }
  category?: {
    name: string;
    description?: string;
  };
  popularKeywords?: string;
  seo?: {
    title: string;
    keywords: string;
    description: string;
  }
}

// 获取TopReview数据
async function getTopReviewData(param: string | number) {
  try {
    let topReviewedData = await topReviewsApi.getTopReview(param)

    // 整理图片
    if (topReviewedData.data.pageList.items) {
      topReviewedData.data.pageList.items.forEach((item: any) => {
        item.imgUrl = item.filename || item.newThumbnail
      })
    }

    return {
      ...topReviewedData.data
    }
  } catch (error) {
    console.log(`top-reviewed${param}  数据获取失败： `, error)
  }
}

// 在服务器端渲染时调用接口，并返回 props 给页面组件
export async function getServerSideProps(context: any) {
  // 获取 URL 中的 参数， param需要加上perPage参数，20条
  const { param } = context.query

  console.log('Next.js :', dayjs().format('YY-MM-DD HH:mm:ss'), `  top-reviewed获取数据中。 参数：top-reviewed${param + '?perPage=20'}`)
  const data = await getTopReviewData(param + '?perPage=20')

  if (data) {
    return {
      props: {
        topReviewsData: data.pageList.items || [],
        topReviewsTotal: data.pageList.total || 0,
        topReviewsCurrentPage: data.pageList.currentPage || 1,
        param: data.param,
        category: data.category,
        popularKeywords: data.popularKeywords,
        seo: data.seo
      }
    }
  } else {
    // 如果没有返回数据，返回一个 notFound 的标志，让 Next.js 渲染一个内置的 404 页面
    return {
      notFound: true,
    }
  }
}

export default function TopReviewed({ topReviewsData, topReviewsTotal, topReviewsCurrentPage, param, category, popularKeywords, seo }: Props) {
  const [csotdData, setCsotdData] = useState<CsotdData | null>()
  const [interestedData, setInterestedData] = useState<ProductItem[]>([]);

  const [midBanner, setMidBanner] = useState<BannerItem | null | undefined>()
  const [midBannerPad, setMidBannerPad] = useState<BannerItem | null | undefined>()

  // 获取csotd模块数据
  async function getCrazyDealData() {
    try {
      let csotdData = await systemApi.getCrazyDealOtd()

      if (csotdData.data) {
        setCsotdData(csotdData.data.product)
      }
    } catch (error) {
      console.log(`csotd数据获取失败： `, error)
    }
  }

  // 获取侧边interested模块数据
  async function getInterestingData() {
    try {
      let resData = await topReviewsApi.getInteresting()

      if (resData.data && resData.data.length) {
        // 整理图片
        resData.data.forEach((item: any) => {
          item.imgUrl = item.filename || item.newThumbnail
        })

        setInterestedData(resData.data)
      }
    } catch (error) {
      console.log(`csotd数据获取失败： `, error)
    }
  }

  // 获取subscribe 图片数据
  const getBannersData = async () => {
    try {
      const res = await systemApi.getFullBanners()
      const { midBanner, midBannerPad } = res.data.home

      setMidBanner(midBanner)
      setMidBannerPad(midBannerPad)
    } catch (error) {
      return { notFound: true }
    }
  }

  // 获取页面数据
  const getPageData = () => {
    // 获取csotd模块数据
    getCrazyDealData()

    // 获取侧边interested模块数据
    getInterestingData()

    // 获取subscribe 图片数据
    getBannersData()
  }

  useEffect(() => {
    getPageData()
  }, [])

  const router = useRouter()
  // 判断是否有aid 或 recoveraid在url，有就将其
  useEffect(() => {
    const { aid, recoveraid }: any = router.query
    let lastAidValue = ''
    let lastRecoveraidValue = ''

    // 检查是否有"aid"参数，如有多个取最后
    if (Array.isArray(aid) && aid.length > 0) {
      lastAidValue = aid[aid.length - 1]
    } else if (typeof aid === 'string') {
      // 如果只有一个"aid"参数，它将是一个字符串
      lastAidValue = aid
    }

    // 检查是否有"recoveraid"参数，如有多个取最后
    if (Array.isArray(recoveraid) && recoveraid.length > 0) {
      lastRecoveraidValue = recoveraid[recoveraid.length - 1]
    } else if (typeof recoveraid === 'string') {
      // 如果只有一个"recoveraid"参数，它将是一个字符串
      lastRecoveraidValue = recoveraid
    }

    // 优先于异步请求，将aid存入cookie, 60秒过期（后续会发起请求重新设置）
    if (lastAidValue || lastRecoveraidValue) {
      let currentDomain = window.location.hostname;
      if(currentDomain == 'www.crazysales.com.au') {
        currentDomain = ".crazysales.com.au"
      }
      setCookie('affiliateid', lastAidValue || lastRecoveraidValue, { expires: 30, secure: true, domain: `${currentDomain}`, })
    }
  }, [router.query])

  // 发现本地有存储aid，重新请求数据
  const getProductDetailWithPromotion = async () => {
    if (!getCookie('affiliateid')) return 

    getPageData()
  }

  // cookie有留存aid参数，则更新Email价格
  useEffect(() => {
    setTimeout(() => {
      let affiliateid = getCookie('affiliateid')
      if (affiliateid) getProductDetailWithPromotion()
    }, 400)
  }, [])

  // 分页器
  const onChange: PaginationProps['onChange'] = (current) => {
    // 转换为小写
    const lowerCaseName = param.categoryName.toLowerCase();
    // 替换符号和空格为-
    const linkName = lowerCaseName.replace(/[\s&,]+/g, '-').replace(/-+/g, '-');

    const linkNameParam = linkName ? '-' + linkName : ''
    let categoryIdParam = param.categoryID > 1 ? '-' + param.categoryID : ''
    
    window.location.href = `/top-reviewed${linkNameParam}${categoryIdParam}-${current || 1}`
  };

  return (
    <>
      <Head>
        <title>{seo?.title || `Top Rated Products Australia | Best Selling Bedding TV Unit Trampoline Swimming Pool Christmas Light`}</title>
        <meta name="keywords" content={seo?.keywords || `Top rated, best selling, best seller, bedding, home decor, christmas lights, tv units, swimming pools, pool, trampoline, massage chair, dog bed, mattress topper, king size bed, electric blanket, xmas lights, bestway pools, kids trampoline, cat house, garden decor, cooling mattress, outdoor christmas lights, sports equipment`}></meta>
        <meta name="description" content={seo?.description || `Enjoy happy shopping and make smart choices with our list of the top-rated products in Australia. Get the best of the best in electronics, beauty, gardening and pet accessories. Click here to find out what’s hot and trending now!`}></meta>
      </Head>

      <DefaultLayout notStickyNavbar={true}>
        <div className={`${styles['page-top-review']}`}>
          <div className={styles['shop-info-box']}>
            <ShopInfo hiddenContactus={true}></ShopInfo>
          </div>
          
          <div className={styles['middle-subscribe-box']}>
            {
              !!(midBanner && midBanner.imgUrl && midBannerPad && midBannerPad.imgUrl) &&
              <MiddleSubscribe midBanner={midBanner} midBannerPad={midBannerPad} />
            }
          </div>

          <div className={styles['page-container']}>
            <div className={styles['category-box']}>
              <CategoryFilter categoryId={param.categoryID} showAdvertise={true}></CategoryFilter>
            </div>

            <div className={styles['reviews-box']}>
              {
                topReviewsData.map((item: ProductItem, index: number) => (
                  <div className={styles['reviews-item']} key={index}>
                    <TopReviewItem {...item}></TopReviewItem>
                  </div>
                ))
              }
            </div>

            <div className={styles['slide-box']}>
              {
                csotdData
                &&
                <CrazySale
                  {
                    ...{ 
                      ...csotdData,
                      imgUrl: csotdData?.filename,
                      promotion: csotdData?.promotion,
                      crazyDeal:csotdData?.crazyDeal
                    }
                  }
                ></CrazySale>
              }

              {
                !!(interestedData && interestedData.length) &&
                <div className={styles['interested-box']}>
                  <div className={styles['interested-title']}>
                    Everyone is interested
                  </div>

                  <div className={styles['interested-list']}>
                    {
                      interestedData.map((item: ProductItem, index: number) => (
                        <div 
                          className={`${styles['interested-item']} ${index == 0 ? styles['first-item'] : ''}`} 
                          key={index}
                        >
                          <InterestedItem {...item} rankNum={index + 1}></InterestedItem>
                        </div>
                      ))
                    }
                  </div>
                </div>
              }
            </div>
          </div>

          {/* 分页器 */}
          {
            !!(topReviewsCurrentPage) &&
            <Pagination
              showSizeChanger={false}
              onChange={onChange}
              defaultCurrent={topReviewsCurrentPage}
              defaultPageSize={20}
              total={topReviewsTotal}
            />
          }

          {
            !!(popularKeywords) &&
            <div className={`${styles['about-box']} margin-top-16`}>
              <div className={styles['popular-category']} dangerouslySetInnerHTML={{ __html: `Popular Category: ${popularKeywords}` }}></div>
            </div>
          }

          {
            !!(category?.description) &&
            <div className={`${styles['about-box']} margin-top-16`}>
              <div className={styles['header']}>
                About&nbsp;{category?.name}
              </div>

              <div className={styles['content']} dangerouslySetInnerHTML={{ __html: category?.description }}></div>
            </div>
          }
          
          <div style={{ marginTop: '20px' }}>
            <GoogleADSense />
          </div>

        </div>
      </DefaultLayout>
    </>
  )
}
